/**
 * nle-export — Phase 25 Increment 4.
 *
 * Real serializers that turn the editor's `TProject` into downloadable
 * NLE / subtitle / data files. Before this the export catalog (`formats.ts`)
 * listed 26 formats but had ZERO serializers behind it — every "내보내기" was a
 * no-op. This module implements the ones that can be produced correctly from
 * the project model: EDL, FCPXML, OpenTimelineIO, SRT, WebVTT, TXT, JSON, CSV.
 *
 * Video formats (mp4/gif) go through the canvas renderer, not here. The
 * remaining vendor-specific NLE formats (CapCut .draft, Premiere XML, DaVinci
 * .drp, Shotcut MLT) are not yet implemented — `serializeProject` returns null
 * for them so the caller can report honestly.
 */

import type { TProject } from "@/project/types";
import type { TimelineTrack, TrackType } from "@/timeline/types";
import { mediaTimeToSeconds, type MediaTime } from "@/wasm";

interface FlatClip {
	trackName: string;
	trackType: TrackType;
	id: string;
	name: string;
	type: string;
	/** seconds */
	timelineStart: number;
	timelineEnd: number;
	sourceIn: number;
	sourceOut: number;
}

export interface SerializedFile {
	filename: string;
	content: string;
	mimeType: string;
	/** Optional binary payload — when present it is downloaded instead of
	 * `content` (used for rendered mp4 buffers, Increment 19). */
	blob?: Blob;
}

/** Flatten every element of every track in every scene into ordered clips. */
function flattenClips(project: TProject): FlatClip[] {
	const clips: FlatClip[] = [];
	for (const scene of project.scenes) {
		const tracks: TimelineTrack[] = [
			scene.tracks.main,
			...scene.tracks.overlay,
			...scene.tracks.audio,
		];
		for (const track of tracks) {
			const elements = track.elements as ReadonlyArray<{
				id: string;
				name: string;
				type: string;
				startTime: MediaTime;
				duration: MediaTime;
				trimStart: MediaTime;
				trimEnd: MediaTime;
			}>;
			for (const el of elements) {
				const start = mediaTimeToSeconds({ time: el.startTime });
				const dur = mediaTimeToSeconds({ time: el.duration });
				const tStart = mediaTimeToSeconds({ time: el.trimStart });
				const tEnd = mediaTimeToSeconds({ time: el.trimEnd });
				clips.push({
					trackName: track.name,
					trackType: track.type,
					id: el.id,
					name: el.name,
					type: el.type,
					timelineStart: start,
					timelineEnd: start + dur,
					sourceIn: tStart,
					sourceOut: tEnd > tStart ? tEnd : tStart + dur,
				});
			}
		}
	}
	return clips.sort((a, b) => a.timelineStart - b.timelineStart);
}

/** FrameRate ({numerator, denominator}) -> frames-per-second number. */
function fpsNum(project: TProject): number {
	const fr = project.settings.fps;
	return fr.denominator > 0 ? fr.numerator / fr.denominator : 30;
}

/** seconds -> HH:MM:SS:FF timecode at the given fps. */
function timecode(seconds: number, fps: number): string {
	const f = Math.max(1, Math.round(fps));
	const total = Math.max(0, seconds);
	const hh = Math.floor(total / 3600);
	const mm = Math.floor((total % 3600) / 60);
	const ss = Math.floor(total % 60);
	const ff = Math.floor((total - Math.floor(total)) * f);
	const p = (n: number) => String(n).padStart(2, "0");
	return `${p(hh)}:${p(mm)}:${p(ss)}:${p(ff)}`;
}

/** seconds -> SRT timestamp HH:MM:SS,mmm */
function srtTime(seconds: number): string {
	const total = Math.max(0, seconds);
	const hh = Math.floor(total / 3600);
	const mm = Math.floor((total % 3600) / 60);
	const ss = Math.floor(total % 60);
	const ms = Math.round((total - Math.floor(total)) * 1000);
	const p = (n: number, w = 2) => String(n).padStart(w, "0");
	return `${p(hh)}:${p(mm)}:${p(ss)},${p(ms, 3)}`;
}

function vttTime(seconds: number): string {
	return srtTime(seconds).replace(",", ".");
}

function xmlEscape(s: string): string {
	return s
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

/** CMX3600 EDL — the most universal NLE interchange format. */
function toEdl(project: TProject): string {
	const fps = fpsNum(project);
	const clips = flattenClips(project);
	const lines: string[] = [
		`TITLE: ${project.metadata.name}`,
		"FCM: NON-DROP FRAME",
		"",
	];
	clips.forEach((c, i) => {
		const num = String(i + 1).padStart(3, "0");
		const chan = c.trackType === "audio" ? "A" : "V";
		lines.push(
			`${num}  AX       ${chan}     C        ` +
				`${timecode(c.sourceIn, fps)} ${timecode(c.sourceOut, fps)} ` +
				`${timecode(c.timelineStart, fps)} ${timecode(c.timelineEnd, fps)}`,
		);
		lines.push(`* FROM CLIP NAME: ${c.name}`);
		lines.push("");
	});
	return lines.join("\n");
}

/** FCPXML 1.10 — Final Cut Pro / DaVinci Resolve import. */
function toFcpxml(project: TProject): string {
	const fps = fpsNum(project);
	const { width, height } = project.settings.canvasSize;
	const clips = flattenClips(project);
	const frameDur = `1/${Math.round(fps)}s`;
	const totalEnd = clips.reduce((m, c) => Math.max(m, c.timelineEnd), 0);
	const assetClips = clips
		.map((c, i) => {
			const offset = `${Math.round(c.timelineStart * fps)}/${Math.round(fps)}s`;
			const dur = `${Math.round((c.timelineEnd - c.timelineStart) * fps)}/${Math.round(fps)}s`;
			return (
				`        <asset-clip name="${xmlEscape(c.name)}" ` +
				`ref="r${i + 2}" offset="${offset}" duration="${dur}" ` +
				`lane="${c.trackType === "audio" ? -1 : 1}"/>`
			);
		})
		.join("\n");
	const assets = clips
		.map(
			(c, i) =>
				`    <asset id="r${i + 2}" name="${xmlEscape(c.name)}" ` +
				`hasVideo="${c.trackType === "audio" ? 0 : 1}" hasAudio="1"/>`,
		)
		.join("\n");
	return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE fcpxml>
<fcpxml version="1.10">
  <resources>
    <format id="r1" name="MML-${width}x${height}" frameDuration="${frameDur}" width="${width}" height="${height}"/>
${assets}
  </resources>
  <library>
    <event name="${xmlEscape(project.metadata.name)}">
      <project name="${xmlEscape(project.metadata.name)}">
        <sequence format="r1" duration="${Math.round(totalEnd * fps)}/${Math.round(fps)}s">
          <spine>
${assetClips}
          </spine>
        </sequence>
      </project>
    </event>
  </library>
</fcpxml>
`;
}

/** OpenTimelineIO (.otio) — Pixar's NLE-agnostic interchange JSON. */
function toOtio(project: TProject): string {
	const fps = fpsNum(project);
	const clips = flattenClips(project);
	const rt = (seconds: number) => ({
		OTIO_SCHEMA: "RationalTime.1",
		rate: fps,
		value: Math.round(seconds * fps),
	});
	const otioClips = clips.map((c) => ({
		OTIO_SCHEMA: "Clip.2",
		name: c.name,
		source_range: {
			OTIO_SCHEMA: "TimeRange.1",
			start_time: rt(c.sourceIn),
			duration: rt(c.timelineEnd - c.timelineStart),
		},
	}));
	return JSON.stringify(
		{
			OTIO_SCHEMA: "Timeline.1",
			name: project.metadata.name,
			tracks: {
				OTIO_SCHEMA: "Stack.1",
				children: [
					{
						OTIO_SCHEMA: "Track.1",
						name: "MML Timeline",
						kind: "Video",
						children: otioClips,
					},
				],
			},
		},
		null,
		2,
	);
}

/** Subtitle / script — uses text-track elements (their `name` is the caption). */
function textClips(project: TProject): FlatClip[] {
	return flattenClips(project).filter((c) => c.type === "text");
}

function toSrt(project: TProject): string {
	const clips = textClips(project);
	if (clips.length === 0) return "";
	return clips
		.map(
			(c, i) =>
				`${i + 1}\n${srtTime(c.timelineStart)} --> ${srtTime(c.timelineEnd)}\n${c.name}\n`,
		)
		.join("\n");
}

function toVtt(project: TProject): string {
	const clips = textClips(project);
	const body = clips
		.map(
			(c) =>
				`${vttTime(c.timelineStart)} --> ${vttTime(c.timelineEnd)}\n${c.name}\n`,
		)
		.join("\n");
	return `WEBVTT\n\n${body}`;
}

function toTxt(project: TProject): string {
	const clips = textClips(project);
	const header = `${project.metadata.name}\n${"=".repeat(project.metadata.name.length)}\n\n`;
	if (clips.length === 0) return `${header}(텍스트 요소 없음)\n`;
	return (
		header +
		clips
			.map((c) => `[${srtTime(c.timelineStart)}] ${c.name}`)
			.join("\n") +
		"\n"
	);
}

function toJson(project: TProject): string {
	const clips = flattenClips(project);
	return JSON.stringify(
		{
			project: project.metadata.name,
			fps: project.settings.fps,
			canvas: project.settings.canvasSize,
			sceneCount: project.scenes.length,
			clips: clips.map((c) => ({
				track: c.trackName,
				trackType: c.trackType,
				name: c.name,
				type: c.type,
				timelineStart: Number(c.timelineStart.toFixed(3)),
				timelineEnd: Number(c.timelineEnd.toFixed(3)),
				sourceIn: Number(c.sourceIn.toFixed(3)),
				sourceOut: Number(c.sourceOut.toFixed(3)),
			})),
		},
		null,
		2,
	);
}

function toCsv(project: TProject): string {
	const clips = flattenClips(project);
	const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
	const rows = [
		"index,track,trackType,name,type,timelineStart,timelineEnd,sourceIn,sourceOut",
		...clips.map((c, i) =>
			[
				i + 1,
				esc(c.trackName),
				c.trackType,
				esc(c.name),
				c.type,
				c.timelineStart.toFixed(3),
				c.timelineEnd.toFixed(3),
				c.sourceIn.toFixed(3),
				c.sourceOut.toFixed(3),
			].join(","),
		),
	];
	return rows.join("\n") + "\n";
}

/** CapCut desktop draft_content.json — importable as a CapCut project. */
function toCapcutDraft(project: TProject): string {
	const fps = fpsNum(project);
	const { width, height } = project.settings.canvasSize;
	const clips = flattenClips(project);
	const us = (seconds: number) => Math.round(seconds * 1_000_000); // CapCut uses microseconds
	const materials = clips.map((c, i) => ({
		id: `mat-${i + 1}`,
		type: c.trackType === "audio" ? "audio" : c.type === "text" ? "text" : "video",
		material_name: c.name,
		duration: us(c.sourceOut - c.sourceIn),
	}));
	const segments = clips.map((c, i) => ({
		id: `seg-${i + 1}`,
		material_id: `mat-${i + 1}`,
		target_timerange: {
			start: us(c.timelineStart),
			duration: us(c.timelineEnd - c.timelineStart),
		},
		source_timerange: {
			start: us(c.sourceIn),
			duration: us(c.sourceOut - c.sourceIn),
		},
	}));
	return JSON.stringify(
		{
			version: "5.0.0",
			id: `mml-${Date.now()}`,
			create_time: Math.floor(Date.now() / 1000),
			fps,
			duration: us(clips.reduce((m, c) => Math.max(m, c.timelineEnd), 0)),
			canvas_config: { width, height, ratio: `${width}:${height}` },
			materials: {
				videos: materials.filter((m) => m.type === "video"),
				audios: materials.filter((m) => m.type === "audio"),
				texts: materials.filter((m) => m.type === "text"),
			},
			tracks: [{ type: "video", segments }],
			extra_info: {
				export_from: "MakeMonLab Studio",
				project_name: project.metadata.name,
			},
		},
		null,
		2,
	);
}

/** Adobe Premiere Pro XML (FCP7 xmeml) — Premiere File > Import. */
function toPremiereXml(project: TProject): string {
	const fps = Math.round(fpsNum(project));
	const { width, height } = project.settings.canvasSize;
	const clips = flattenClips(project);
	const fr = (seconds: number) => Math.round(seconds * fps);
	const totalEnd = clips.reduce((m, c) => Math.max(m, c.timelineEnd), 0);
	const clipItems = clips
		.map(
			(c, i) =>
				`          <clipitem id="clipitem-${i + 1}">\n` +
				`            <name>${xmlEscape(c.name)}</name>\n` +
				`            <start>${fr(c.timelineStart)}</start>\n` +
				`            <end>${fr(c.timelineEnd)}</end>\n` +
				`            <in>${fr(c.sourceIn)}</in>\n` +
				`            <out>${fr(c.sourceOut)}</out>\n` +
				`          </clipitem>`,
		)
		.join("\n");
	return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE xmeml>
<xmeml version="4">
  <sequence id="${xmlEscape(project.metadata.name)}">
    <name>${xmlEscape(project.metadata.name)}</name>
    <duration>${fr(totalEnd)}</duration>
    <rate><timebase>${fps}</timebase><ntsc>FALSE</ntsc></rate>
    <media>
      <video>
        <format>
          <samplecharacteristics>
            <width>${width}</width>
            <height>${height}</height>
          </samplecharacteristics>
        </format>
        <track>
${clipItems}
        </track>
      </video>
    </media>
  </sequence>
</xmeml>
`;
}

/** Shotcut MLT XML — Shotcut File > Open. */
function toShotcutMlt(project: TProject): string {
	const fps = Math.round(fpsNum(project));
	const { width, height } = project.settings.canvasSize;
	const clips = flattenClips(project);
	const tc = (seconds: number) => timecode(seconds, fps);
	const producers = clips
		.map(
			(c, i) =>
				`  <producer id="producer-${i + 1}" in="${tc(c.sourceIn)}" out="${tc(c.sourceOut)}">\n` +
				`    <property name="mlt_service">blank</property>\n` +
				`    <property name="shotcut:name">${xmlEscape(c.name)}</property>\n` +
				`  </producer>`,
		)
		.join("\n");
	const entries = clips
		.map(
			(c, i) =>
				`    <entry producer="producer-${i + 1}" in="${tc(c.sourceIn)}" out="${tc(c.sourceOut)}"/>`,
		)
		.join("\n");
	return `<?xml version="1.0" encoding="utf-8"?>
<mlt LC_NUMERIC="C" version="7.0.0" title="${xmlEscape(project.metadata.name)}">
  <profile description="MML ${width}x${height}" width="${width}" height="${height}" frame_rate_num="${fps}" frame_rate_den="1"/>
${producers}
  <playlist id="main_playlist">
${entries}
  </playlist>
  <tractor id="tractor0">
    <track producer="main_playlist"/>
  </tractor>
</mlt>
`;
}

const SERIALIZERS: Record<
	string,
	{ ext: string; mime: string; fn: (p: TProject) => string }
> = {
	edl: { ext: "edl", mime: "text/plain", fn: toEdl },
	fcpxml: { ext: "fcpxml", mime: "application/xml", fn: toFcpxml },
	otio: { ext: "otio", mime: "application/json", fn: toOtio },
	"capcut-draft": {
		ext: "draft_content.json",
		mime: "application/json",
		fn: toCapcutDraft,
	},
	"premiere-xml": { ext: "xml", mime: "application/xml", fn: toPremiereXml },
	"shotcut-mlt": { ext: "mlt", mime: "application/xml", fn: toShotcutMlt },
	srt: { ext: "srt", mime: "text/plain", fn: toSrt },
	vtt: { ext: "vtt", mime: "text/vtt", fn: toVtt },
	txt: { ext: "txt", mime: "text/plain", fn: toTxt },
	json: { ext: "json", mime: "application/json", fn: toJson },
	csv: { ext: "csv", mime: "text/csv", fn: toCsv },
};

/** Format keys this module can actually serialize. */
export const SERIALIZABLE_FORMATS = Object.keys(SERIALIZERS);

/**
 * Serialize the project for one export format key. Returns null for formats
 * with no serializer yet (mp4/gif renderer formats, vendor-specific NLE
 * formats) so the caller can report honestly instead of pretending.
 */
export function serializeProject(
	formatKey: string,
	project: TProject,
): SerializedFile | null {
	const s = SERIALIZERS[formatKey];
	if (!s) return null;
	const safeName =
		project.metadata.name.replace(/[^\w가-힣.-]+/g, "_").slice(0, 60) ||
		"project";
	return {
		filename: `${safeName}.${s.ext}`,
		content: s.fn(project),
		mimeType: s.mime,
	};
}

/** Trigger a browser download of a serialized file (client-side only). */
export function downloadSerializedFile(file: SerializedFile): void {
	const blob =
		file.blob ?? new Blob([file.content], { type: file.mimeType });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = file.filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}
